import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProgram, createApplication, calculateLoan } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { EdFiLogo } from '../components/EdFiLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, ArrowRight, Check, FileText, User, Briefcase, 
  CreditCard, Upload, Loader2, CheckCircle, AlertCircle, Building2
} from 'lucide-react';

const STEPS = [
  { id: 1, name: 'Programa', icon: Building2 },
  { id: 2, name: 'Datos Personales', icon: User },
  { id: 3, name: 'Empleo', icon: Briefcase },
  { id: 4, name: 'Documentos', icon: FileText },
  { id: 5, name: 'Verificación', icon: CreditCard },
  { id: 6, name: 'Resultado', icon: CheckCircle }
];

const MEXICAN_STATES = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche",
  "Chiapas", "Chihuahua", "Ciudad de México", "Coahuila", "Colima",
  "Durango", "Guanajuato", "Guerrero", "Hidalgo", "Jalisco", "México",
  "Michoacán", "Morelos", "Nayarit", "Nuevo León", "Oaxaca", "Puebla",
  "Querétaro", "Quintana Roo", "San Luis Potosí", "Sinaloa", "Sonora",
  "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán", "Zacatecas"
];

const ApplicationWizard = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [program, setProgram] = useState(null);
  const [application, setApplication] = useState(null);
  const [loanCalc, setLoanCalc] = useState(null);
  const [kycResult, setKycResult] = useState(null);
  const [creditResult, setCreditResult] = useState(null);
  const [error, setError] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    // Personal
    curp: '',
    rfc: '',
    ine_number: '',
    birth_date: '',
    gender: '',
    marital_status: '',
    // Address
    street: '',
    number: '',
    colony: '',
    postal_code: '',
    city: '',
    state: '',
    // Employment
    employment_status: '',
    company_name: '',
    position: '',
    monthly_income: '',
    employment_years: '',
    // Loan
    term_months: '12',
    down_payment_percent: '10'
  });

  const [documents, setDocuments] = useState({
    ine_front: null,
    ine_back: null,
    proof_income: null,
    proof_address: null
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { redirectTo: `/apply/${programId}` } });
      return;
    }
    fetchProgram();
  }, [programId, isAuthenticated, navigate]);

  const fetchProgram = async () => {
    try {
      const response = await getProgram(programId);
      setProgram(response.data);
      
      // Calculate initial loan
      const amount = response.data.price * 0.9;
      const calcResponse = await calculateLoan({
        amount,
        term_months: 12,
        scoring: 'promedio'
      });
      setLoanCalc(calcResponse.data);
    } catch (error) {
      console.error('Error fetching program:', error);
      navigate('/programs');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (type, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setDocuments(prev => ({
        ...prev,
        [type]: {
          name: file.name,
          data: reader.result.split(',')[1], // Remove data:...;base64, prefix
          uploaded: true
        }
      }));
    };
    reader.readAsDataURL(file);
  };

  const createApplicationRecord = async () => {
    setLoading(true);
    setError('');
    
    try {
      const amount = program.price * (1 - parseFloat(formData.down_payment_percent) / 100);
      
      const response = await createApplication({
        program_id: programId,
        amount_requested: amount,
        term_months: parseInt(formData.term_months),
        down_payment_percent: parseFloat(formData.down_payment_percent),
        personal_data: {
          curp: formData.curp,
          rfc: formData.rfc,
          ine_number: formData.ine_number,
          birth_date: formData.birth_date,
          gender: formData.gender,
          marital_status: formData.marital_status,
          address: {
            street: formData.street,
            number: formData.number,
            colony: formData.colony,
            postal_code: formData.postal_code,
            city: formData.city,
            state: formData.state
          }
        },
        employment_data: {
          status: formData.employment_status,
          company_name: formData.company_name,
          position: formData.position,
          monthly_income: parseFloat(formData.monthly_income),
          employment_years: parseFloat(formData.employment_years)
        }
      });
      
      setApplication(response.data);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear la solicitud');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const uploadDocuments = async (appId) => {
    for (const [type, doc] of Object.entries(documents)) {
      if (doc && doc.data) {
        try {
          await api.post(`/applications/${appId}/documents`, {
            document_type: type,
            file_data: doc.data,
            file_name: doc.name
          });
        } catch (err) {
          console.error(`Error uploading ${type}:`, err);
        }
      }
    }
  };

  const runKYC = async (appId) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post(`/applications/${appId}/kyc`, {
        curp: formData.curp,
        rfc: formData.rfc,
        ine_number: formData.ine_number,
        ine_cic: '',
        address: {
          street: formData.street,
          number: formData.number,
          colony: formData.colony,
          postal_code: formData.postal_code,
          city: formData.city,
          state: formData.state
        }
      });
      
      setKycResult(response.data.kyc_result);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Error en verificación KYC');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const runCreditCheck = async (appId) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post(`/applications/${appId}/credit-check`, {
        application_id: appId,
        monthly_income: parseFloat(formData.monthly_income)
      });
      
      setCreditResult(response.data);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Error en verificación crediticia');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    setError('');
    
    if (currentStep === 3) {
      // After employment, create application
      try {
        const app = await createApplicationRecord();
        setCurrentStep(4);
      } catch (err) {
        return;
      }
    } else if (currentStep === 4) {
      // After documents, upload them
      if (application) {
        await uploadDocuments(application.application_id);
      }
      setCurrentStep(5);
    } else if (currentStep === 5) {
      // Run KYC and Credit Check
      try {
        await runKYC(application.application_id);
        const result = await runCreditCheck(application.application_id);
        setCurrentStep(6);
      } catch (err) {
        return;
      }
    } else {
      setCurrentStep(prev => Math.min(prev + 1, 6));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return true;
      case 2:
        return formData.curp && formData.rfc && formData.ine_number && 
               formData.street && formData.postal_code && formData.city && formData.state;
      case 3:
        return formData.employment_status && formData.monthly_income;
      case 4:
        return documents.ine_front && documents.proof_income;
      case 5:
        return true;
      default:
        return true;
    }
  };

  if (!program) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <EdFiLogo size="sm" linkTo="/" />
            <Link to={`/programs/${programId}`} className="text-slate-600 hover:text-brand-blue flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Volver al programa
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-slate-200 py-6">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex flex-col items-center ${index < STEPS.length - 1 ? 'w-full' : ''}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-green-500 text-white' :
                      isActive ? 'bg-brand-blue text-white' :
                      'bg-slate-200 text-slate-500'
                    }`}>
                      {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`text-xs mt-2 ${isActive ? 'text-brand-blue font-medium' : 'text-slate-500'}`}>
                      {step.name}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`h-0.5 w-16 mx-2 ${currentStep > step.id ? 'bg-green-500' : 'bg-slate-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="pt-6">
                {/* Step 1: Program Summary */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-slate-900">Confirma tu programa</h2>
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{program.name}</h3>
                      <p className="text-slate-600 mb-4">{program.university?.name}</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-500">Precio del programa</p>
                          <p className="text-xl font-bold text-slate-900">{formatPrice(program.price)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Duración</p>
                          <p className="text-xl font-bold text-slate-900">{program.duration_months} meses</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <Label>Plazo de financiamiento</Label>
                        <Select value={formData.term_months} onValueChange={(v) => handleChange('term_months', v)}>
                          <SelectTrigger className="mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="3">3 meses</SelectItem>
                            <SelectItem value="6">6 meses</SelectItem>
                            <SelectItem value="9">9 meses</SelectItem>
                            <SelectItem value="12">12 meses</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Enganche (%)</Label>
                        <Select value={formData.down_payment_percent} onValueChange={(v) => handleChange('down_payment_percent', v)}>
                          <SelectTrigger className="mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10%</SelectItem>
                            <SelectItem value="20">20%</SelectItem>
                            <SelectItem value="30">30%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Personal Data */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-slate-900">Datos Personales</h2>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>CURP *</Label>
                        <Input
                          value={formData.curp}
                          onChange={(e) => handleChange('curp', e.target.value.toUpperCase())}
                          placeholder="18 caracteres"
                          maxLength={18}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>RFC *</Label>
                        <Input
                          value={formData.rfc}
                          onChange={(e) => handleChange('rfc', e.target.value.toUpperCase())}
                          placeholder="13 caracteres"
                          maxLength={13}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Número de INE (CIC) *</Label>
                        <Input
                          value={formData.ine_number}
                          onChange={(e) => handleChange('ine_number', e.target.value)}
                          placeholder="Número de credencial"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Fecha de nacimiento</Label>
                        <Input
                          type="date"
                          value={formData.birth_date}
                          onChange={(e) => handleChange('birth_date', e.target.value)}
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <h3 className="text-lg font-semibold text-slate-900 mt-6">Dirección</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Calle *</Label>
                        <Input
                          value={formData.street}
                          onChange={(e) => handleChange('street', e.target.value)}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Número *</Label>
                        <Input
                          value={formData.number}
                          onChange={(e) => handleChange('number', e.target.value)}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Colonia</Label>
                        <Input
                          value={formData.colony}
                          onChange={(e) => handleChange('colony', e.target.value)}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Código Postal *</Label>
                        <Input
                          value={formData.postal_code}
                          onChange={(e) => handleChange('postal_code', e.target.value)}
                          maxLength={5}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Ciudad *</Label>
                        <Input
                          value={formData.city}
                          onChange={(e) => handleChange('city', e.target.value)}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Estado *</Label>
                        <Select value={formData.state} onValueChange={(v) => handleChange('state', v)}>
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Selecciona..." />
                          </SelectTrigger>
                          <SelectContent>
                            {MEXICAN_STATES.map(state => (
                              <SelectItem key={state} value={state}>{state}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Employment */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-slate-900">Información Laboral</h2>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Situación laboral *</Label>
                        <Select value={formData.employment_status} onValueChange={(v) => handleChange('employment_status', v)}>
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Selecciona..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="empleado">Empleado</SelectItem>
                            <SelectItem value="independiente">Independiente/Freelance</SelectItem>
                            <SelectItem value="empresario">Empresario</SelectItem>
                            <SelectItem value="estudiante">Estudiante</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Ingreso mensual (MXN) *</Label>
                        <Input
                          type="number"
                          value={formData.monthly_income}
                          onChange={(e) => handleChange('monthly_income', e.target.value)}
                          placeholder="Ej: 25000"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Empresa/Negocio</Label>
                        <Input
                          value={formData.company_name}
                          onChange={(e) => handleChange('company_name', e.target.value)}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Puesto</Label>
                        <Input
                          value={formData.position}
                          onChange={(e) => handleChange('position', e.target.value)}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Años en empleo actual</Label>
                        <Input
                          type="number"
                          value={formData.employment_years}
                          onChange={(e) => handleChange('employment_years', e.target.value)}
                          className="mt-2"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Documents */}
                {currentStep === 4 && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-slate-900">Documentos</h2>
                    <p className="text-slate-600">Sube los documentos requeridos para verificar tu identidad</p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      {[
                        { key: 'ine_front', label: 'INE - Frente *', required: true },
                        { key: 'ine_back', label: 'INE - Reverso', required: false },
                        { key: 'proof_income', label: 'Comprobante de ingresos *', required: true },
                        { key: 'proof_address', label: 'Comprobante de domicilio', required: false }
                      ].map(doc => (
                        <div key={doc.key} className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-brand-blue transition-colors">
                          {documents[doc.key]?.uploaded ? (
                            <div className="text-green-600">
                              <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                              <p className="font-medium">{documents[doc.key].name}</p>
                              <button 
                                onClick={() => setDocuments(prev => ({ ...prev, [doc.key]: null }))}
                                className="text-sm text-slate-500 hover:text-red-500 mt-2"
                              >
                                Eliminar
                              </button>
                            </div>
                          ) : (
                            <label className="cursor-pointer">
                              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                              <p className="font-medium text-slate-700">{doc.label}</p>
                              <p className="text-sm text-slate-500">Click para subir</p>
                              <input
                                type="file"
                                accept="image/*,.pdf"
                                onChange={(e) => handleFileChange(doc.key, e)}
                                className="hidden"
                              />
                            </label>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 5: Verification */}
                {currentStep === 5 && (
                  <div className="space-y-6 text-center py-8">
                    <h2 className="text-2xl font-bold text-slate-900">Verificación</h2>
                    <p className="text-slate-600">
                      Al continuar, verificaremos tu identidad y consultaremos tu historial crediticio.
                    </p>
                    
                    <div className="bg-slate-50 rounded-xl p-6 text-left space-y-4">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span>Verificación de CURP con RENAPO</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span>Verificación de RFC con SAT</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span>Validación de INE con INE</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span>Consulta a Buró de Crédito</span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-slate-500">
                      Al presionar "Verificar", autorizas la consulta a Buró de Crédito
                    </p>
                  </div>
                )}

                {/* Step 6: Result */}
                {currentStep === 6 && creditResult && (
                  <div className="space-y-6 text-center py-8">
                    {creditResult.status === 'approved' ? (
                      <>
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                          <CheckCircle className="w-10 h-10 text-green-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-green-600">¡Felicidades!</h2>
                        <p className="text-xl text-slate-700">Tu crédito ha sido aprobado</p>
                        
                        <div className="bg-green-50 rounded-xl p-6 text-left">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-slate-500">Monto aprobado</p>
                              <p className="text-2xl font-bold text-green-600">
                                {formatPrice(creditResult.amount_approved)}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-500">Tasa mensual</p>
                              <p className="text-2xl font-bold text-slate-900">
                                {(creditResult.interest_rate * 100).toFixed(1)}%
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-500">Pago mensual</p>
                              <p className="text-2xl font-bold text-slate-900">
                                {formatPrice(creditResult.monthly_payment)}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-500">Score crediticio</p>
                              <p className="text-2xl font-bold text-slate-900">
                                {creditResult.credit_result?.credit_score}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <Button
                          onClick={() => navigate('/dashboard')}
                          className="bg-brand-magenta hover:bg-brand-magenta-dark text-white px-8 py-6 text-lg"
                        >
                          Ir a mi Dashboard
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                          <AlertCircle className="w-10 h-10 text-red-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-red-600">Solicitud No Aprobada</h2>
                        <p className="text-xl text-slate-700">
                          {creditResult.credit_result?.decision?.reason || 'No cumples con los requisitos mínimos'}
                        </p>
                        
                        <div className="bg-slate-50 rounded-xl p-6 text-left">
                          <p className="text-slate-600">
                            Puedes volver a aplicar en 90 días o mejorar tu perfil crediticio.
                          </p>
                        </div>
                        
                        <Button
                          variant="outline"
                          onClick={() => navigate('/programs')}
                          className="px-8 py-6"
                        >
                          Ver otros programas
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg">Resumen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500">Programa</p>
                  <p className="font-semibold">{program.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Precio</p>
                  <p className="font-semibold">{formatPrice(program.price)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Enganche ({formData.down_payment_percent}%)</p>
                  <p className="font-semibold">
                    {formatPrice(program.price * parseFloat(formData.down_payment_percent) / 100)}
                  </p>
                </div>
                <div className="border-t pt-4">
                  <p className="text-sm text-slate-500">A financiar</p>
                  <p className="text-2xl font-bold text-brand-blue">
                    {formatPrice(program.price * (1 - parseFloat(formData.down_payment_percent) / 100))}
                  </p>
                </div>
                {loanCalc && (
                  <div className="bg-brand-blue/5 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Pago mensual estimado</p>
                    <p className="text-xl font-bold text-brand-blue">
                      {formatPrice(loanCalc.monthly_payment)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      * Tasa y monto sujetos a aprobación
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Navigation Buttons */}
        {currentStep < 6 && (
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1 || loading}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Anterior
            </Button>
            <Button
              onClick={handleNext}
              disabled={!canProceed() || loading}
              className="bg-brand-magenta hover:bg-brand-magenta-dark text-white"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {currentStep === 5 ? 'Verificar y Solicitar' : 'Siguiente'}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationWizard;
